import React, { useState, useEffect } from "react";
import api, { HR_BASE_URL } from "../api";
import styled, { keyframes } from "styled-components";
import { Clock, Briefcase, LogIn, CheckCircle, CalendarDays, History, Sunrise, Sparkles, Activity } from "lucide-react";

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
  50% { box-shadow: 0 0 20px 0 rgba(99, 102, 241, 0.6); }
`;

const AnimatedSparkles = styled(Sparkles)`
  display: inline;
  animation: ${float} 2s infinite ease-in-out;
`;

// --- Styled Components ---
const Page = styled.div`
  min-height: 100vh;
  padding: clamp(20px, 4vw, 40px);
  background: radial-gradient(circle at top left, rgba(99, 102, 241, 0.05), transparent 40%),
              radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.05), transparent 40%);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out forwards;
`;

const Header = styled.div`
  margin-bottom: 40px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 20px;
`;

const WelcomeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Greeting = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  animation: ${float} 3s ease-in-out infinite;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 800;
  background: linear-gradient(135deg, #fff, #94a3b8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 4px 0 0 0;
  color: var(--muted);
  font-size: 15px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const StatCard = styled.div`
  background: var(--glass);
  backdrop-filter: blur(20px) saturate(200%);
  -webkit-backdrop-filter: blur(20px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${(props) => props.color1 || 'var(--primary)'}, ${(props) => props.color2 || 'var(--accent)'});
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.15);
    
    &::before {
      opacity: 1;
    }
  }
`;

const StatIconWrapper = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  background: ${(props) => props.bg || "rgba(255, 255, 255, 0.05)"};
  color: ${(props) => props.color || "var(--text)"};
  box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.05);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 18px;
    background: ${(props) => props.color || "transparent"};
    filter: blur(10px);
    opacity: 0.2;
    z-index: -1;
  }
`;

const StatValue = styled.h3`
  margin: 0;
  font-size: clamp(26px, 3vw, 34px);
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  letter-spacing: -0.5px;
`;

const StatLabel = styled.p`
  margin: 6px 0 0 0;
  font-size: 14px;
  color: var(--muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    opacity: 0.8;
  }
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-top: 40px;
  
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const GlassPanel = styled.div`
  background: linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 15px 35px -15px rgba(0, 0, 0, 0.4);
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const LogDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 10px;
`;

const LogItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.03);
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
    background: rgba(0, 0, 0, 0.3);
  }
`;

const LogLabel = styled.span`
  color: var(--muted);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`;

const LogValue = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #fff;
`;

const ShiftList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: 280px;
  padding-right: 8px;
  
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
`;

const ShiftItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    transform: translateX(4px);
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  background: ${(props) => {
    switch(props.status) {
      case "Present": return "linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.1))";
      case "Absent": return "linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1))";
      case "Week Off": return "linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1))";
      default: return "linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.1))";
    }
  }};
  color: ${(props) => {
    switch(props.status) {
      case "Present": return "#10b981";
      case "Absent": return "#ef4444";
      case "Week Off": return "#a78bfa";
      default: return "#f59e0b";
    }
  }};
  border: 1px solid ${(props) => {
    switch(props.status) {
      case "Present": return "rgba(16, 185, 129, 0.3)";
      case "Absent": return "rgba(239, 68, 68, 0.3)";
      case "Week Off": return "rgba(139, 92, 246, 0.3)";
      default: return "rgba(245, 158, 11, 0.3)";
    }
  }};
`;

const Loader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50vh;
  color: var(--primary);
  font-weight: 600;
  font-size: 18px;
  gap: 12px;
  animation: ${pulseGlow} 2s infinite;
  padding: 20px 40px;
  border-radius: 30px;
  background: rgba(99, 102, 241, 0.1);
  width: fit-content;
  margin: 0 auto;
`;


function EmployeeDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("Employee");

  const fetchStatus = async () => {
    try {
      const empId = localStorage.getItem("employee_id");
      const name = localStorage.getItem("name") || "Employee";
      setUserName(name.split(" ")[0]); // Get first name

      if (!empId) {
        console.error("No employee ID found in local storage");
        setLoading(false);
        return;
      }
      const response = await api.get(`${HR_BASE_URL}employee/today-status/?employee_id=${empId}`);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching today status", err);
    } finally {
      // Add slight delay for smooth animation reveal
      setTimeout(() => setLoading(false), 500);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <Page>
      <Container>
        <Header>
          <WelcomeWrapper>
            <Greeting>
              <Sunrise size={18} />
              {getGreeting()}
            </Greeting>
            <Title>Welcome back, {userName}! <AnimatedSparkles size={32} color="#f59e0b" /></Title>
            <Subtitle>Here's what's happening with your schedule today.</Subtitle>
          </WelcomeWrapper>
        </Header>

        {loading ? (
          <Loader>
            <Activity className="animate-spin" size={24} /> Loading Dashboard...
          </Loader>
        ) : data ? (
          <>
            <StatsGrid>
              <StatCard color1="#3b82f6" color2="#60a5fa">
                <StatIconWrapper bg="rgba(59, 130, 246, 0.15)" color="#60a5fa">
                  <Briefcase size={26} strokeWidth={2.5} />
                </StatIconWrapper>
                <StatValue>{data.shift_name}</StatValue>
                <StatLabel>Today's Shift</StatLabel>
              </StatCard>

              <StatCard color1="#8b5cf6" color2="#a855f7">
                <StatIconWrapper bg="rgba(139, 92, 246, 0.15)" color="#a78bfa">
                  <Clock size={26} strokeWidth={2.5} />
                </StatIconWrapper>
                <StatValue>{data.shift_times || "N/A"}</StatValue>
                <StatLabel>Shift Timing</StatLabel>
              </StatCard>

              <StatCard color1="#10b981" color2="#34d399">
                <StatIconWrapper bg="rgba(16, 185, 129, 0.15)" color="#34d399">
                  <LogIn size={26} strokeWidth={2.5} />
                </StatIconWrapper>
                <StatValue>{data.login_time}</StatValue>
                <StatLabel>Punched In At</StatLabel>
              </StatCard>

              <StatCard color1="#f59e0b" color2="#fbbf24">
                <StatIconWrapper bg="rgba(245, 158, 11, 0.15)" color="#fbbf24">
                  <CheckCircle size={26} strokeWidth={2.5} />
                </StatIconWrapper>
                <StatValue>{data.current_status}</StatValue>
                <StatLabel>Current Status</StatLabel>
              </StatCard>
            </StatsGrid>

            <Grid2>
              <GlassPanel>
                <SectionTitle><History size={22} color="#60a5fa" /> Yesterday's Log</SectionTitle>
                <LogDetails>
                  <LogItem>
                    <LogLabel>Assigned Shift</LogLabel>
                    <LogValue>{data.yesterday?.shift_name || "N/A"}</LogValue>
                  </LogItem>
                  <LogItem style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <LogLabel>Attendance Status</LogLabel>
                    <Badge status={data.yesterday?.status}>{data.yesterday?.status || "Unknown"}</Badge>
                  </LogItem>
                </LogDetails>
              </GlassPanel>

              <GlassPanel>
                <SectionTitle><CalendarDays size={22} color="#a78bfa" /> Upcoming Schedule</SectionTitle>
                <ShiftList>
                  {data.upcoming_shifts && data.upcoming_shifts.length > 0 ? (
                    data.upcoming_shifts.map((shift, idx) => (
                      <ShiftItem key={idx}>
                        <div>
                          <div style={{ fontWeight: 700, color: '#fff', fontSize: '15px', marginBottom: '4px' }}>{shift.day}</div>
                          <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>{shift.date}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 700, color: "var(--primary)", fontSize: '15px', marginBottom: '4px' }}>{shift.shift}</div>
                          <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>{shift.times || "No timing"}</div>
                        </div>
                      </ShiftItem>
                    ))
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)', background: 'rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                      No upcoming shifts scheduled.
                    </div>
                  )}
                </ShiftList>
              </GlassPanel>
            </Grid2>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--muted)', background: 'var(--glass)', borderRadius: '20px' }}>
            No dashboard data available for today.
          </div>
        )}
      </Container>
    </Page>
  );
}

export default EmployeeDashboard;
